import { GeoJsonLayer, BitmapLayer, IconLayer, TextLayer } from '@deck.gl/layers/typed'
import { MapboxLayer } from '@deck.gl/mapbox/typed'

import { Position } from '@turf/turf'
import * as turf from '@turf/turf'

import { addLogEvent, getMissionEntities } from './mission.service'
import { area_types } from '../config/constant'
import allActions from '../redux/actions'

import { droneUpdateSpeed } from '../config/config'

let interval: ReturnType<typeof setInterval>
let detectionsInterval: ReturnType<typeof setInterval>
let selectedDetectedEntity: string = ''

// LAYER IDS
const layerIds = {
    blueDronesMarkers: 'deck_layer_blue_drones',
    blueDronesText: 'deck_layer_blue_drones_text',
    blueGcs: 'deck_layer_blue_gcs',
    blueGcsText: 'deck_layer_blue_gcs_text',
    detectionMarkers: 'deck_layer_detection',
    detectionText: 'deck_layer_detection_text',
    areas: 'deck_layer_area_',
    radar: 'deck_layer_area_radar',
}

// ICON IMAGES
import uavBlue from '../assets/images/uav-blue.svg'
import uavBlueSelected from '../assets/images/uav-blue-selected.svg'
import uavNeutral from '../assets/images/uav-neutral.svg'
import uavNeutralSelected from '../assets/images/uav-neutral-selected.svg'
import otherNeutral from '../assets/images/other-neutral.svg'
import otherNeutralSelected from '../assets/images/other-neutral-selected.svg'
import unknown from '../assets/images/unknown.svg'
import unknownSelected from '../assets/images/unknown-selected.svg'
import blueGcsImage from '../assets/images/gcs-blue.svg'
import radarImage from '../assets/images/radar-area.svg'
import entityFriendly from '../assets/images/entity-friendly.svg'
import entityFriendlySelected from '../assets/images/entity-friendly-selected.svg'
import entityUncertain from '../assets/images/entity-uncertain.svg'
import entityUncertainSelected from '../assets/images/entity-uncertain-selected.svg'
import entitySuspect from '../assets/images/entity-suspect.svg'
import entitySuspectSelected from '../assets/images/entity-suspect-selected.svg'
import entityUnclassified from '../assets/images/entity-unclassified.svg'
import entityUnclassifiedSelected from '../assets/images/entity-unclassified-selected.svg'
import { AnyAction, Dispatch } from 'redux'
import { Asset } from 'src/redux/reducers/assets'
import { AnyLayer, Map } from 'mapbox-gl'

// AREA COLORS
const area_yellow = {
    fill: [202, 174, 29, 100],
    line: [202, 174, 29, 255],
}
const area_red = {
    fill: [179, 24, 0, 100],
    line: [179, 24, 0, 255],
}
const area_gray = {
    fill: [179, 24, 0, 100],
    line: [179, 24, 0, 255],
}

// LAYER CREATION METHODS

export function createBlueDronesLayer(mapObject: Map, blueAssetData: Asset[], dispatch: Dispatch<AnyAction>) {
    mapObject.addLayer(
        new MapboxLayer({
            // @ts-ignore 2025-05-26 Note ST - Getting an error here about non-existent types, but still seems OK.
            // `@deck.gl` type definitions were still a WIP in v8.x, so warning is to be taken with a grain of salt.
            // Source - https://deck.gl/docs/get-started/using-with-typescript
            type: IconLayer,
            id: layerIds.blueDronesMarkers,
            data: blueAssetData,
            getIcon: (_d: Asset) => ({ url: uavBlue, width: 1000, height: 1250 }),
            pickable: true,
            getSize: 70,
            billboard: false,
            getPosition: (d: Asset) => {
                let position = []
                position.push(d.waypoints[0].longitude)
                position.push(d.waypoints[0].latitude)
                return position
            },
            onClick: (info: any) => {
                handleBlueDroneOnClick(mapObject, info.object.name, dispatch)
            },
        }),
    )

    mapObject.addLayer(
        new MapboxLayer({
            // @ts-ignore 2025-05-26 Note ST - Getting an error here about non-existent types, but still seems OK.
            // `@deck.gl` type definitions were still a WIP in v8.x, so warning is to be taken with a grain of salt.
            // Source - https://deck.gl/docs/get-started/using-with-typescript
            type: TextLayer,
            id: layerIds.blueDronesText,
            data: blueAssetData,
            getPosition: (d: Asset) => {
                let position = []
                position.push(d.waypoints[0].longitude)
                position.push(d.waypoints[0].latitude)
                return position
            },
            getText: (d: Asset) => d.name,
            getSize: 12,
            getAngle: 0,
            billboard: true,
            getPixelOffset: [0, 25],
            fontSettings: { sdf: true },
            getColor: [255, 255, 255, 255],
            outlineColor: [0, 0, 0, 255],
            outlineWidth: 1,
            getTextAnchor: 'middle',
        }),
    )

    return layerIds.blueDronesMarkers
}

export function createBlueGcsLayer(mapObject: Map, blueAssetData: Asset[], firstLabelLayerId: string) {
    mapObject.addLayer(
        new MapboxLayer({
            // @ts-ignore 2025-05-26 Note ST - Getting an error here about non-existent types, but still seems OK.
            // `@deck.gl` type definitions were still a WIP in v8.x, so warning is to be taken with a grain of salt.
            // Source - https://deck.gl/docs/get-started/using-with-typescript
            type: IconLayer,
            id: layerIds.blueGcs,
            data: blueAssetData,
            getIcon: (_d: Asset) => ({ url: blueGcsImage, width: 1000, height: 1250 }),
            pickable: false,
            getSize: 70,
            billboard: false,
            getPosition: (d: Asset) => {
                let position = []
                position.push(d.waypoints[0].longitude)
                position.push(d.waypoints[0].latitude)
                return position
            },
        }),
        firstLabelLayerId,
    )

    mapObject.addLayer(
        new MapboxLayer({
            // @ts-ignore 2025-05-26 Note ST - Getting an error here about non-existent types, but still seems OK.
            // `@deck.gl` type definitions were still a WIP in v8.x, so warning is to be taken with a grain of salt.
            // Source - https://deck.gl/docs/get-started/using-with-typescript
            type: TextLayer,
            id: layerIds.blueGcsText,
            data: blueAssetData,
            getPosition: (d: Asset) => {
                let position = []
                position.push(d.waypoints[0].longitude)
                position.push(d.waypoints[0].latitude)
                return position
            },
            getText: (d: Asset) => d.name,
            getSize: 12,
            getAngle: 0,
            billboard: true,
            getPixelOffset: [0, 25],
            fontSettings: { sdf: true },
            getColor: [255, 255, 255, 255],
            outlineColor: [0, 0, 0, 255],
            outlineWidth: 1,
            getTextAnchor: 'middle',
        }),
    )

    return layerIds.blueGcs
}

export function createDetectionsLayer(mapObject: Map, activeSessionId: number, detectionData: any, dispatch: Dispatch<AnyAction>) {
    mapObject.addLayer(
        new MapboxLayer({
            // @ts-ignore 2025-05-26 Note ST - Getting an error here about non-existent types, but still seems OK.
            // `@deck.gl` type definitions were still a WIP in v8.x, so warning is to be taken with a grain of salt.
            // Source - https://deck.gl/docs/get-started/using-with-typescript
            type: IconLayer,
            id: layerIds.detectionMarkers,
            data: detectionData,
            getIcon: (d: Asset) => {
                switch (d.alliance) {
                    case 'Enemy':
                        switch (d.threat_level) {
                            case 'Unclassified':
                                return { url: entityUnclassifiedSelected, width: 48, height: 48 }
                            case 'Friendly':
                                return { url: entityFriendlySelected, width: 48, height: 48 }
                            case 'Uncertain':
                                return { url: entityUncertainSelected, width: 48, height: 48 }
                            case 'Suspect':
                                return { url: entitySuspectSelected, width: 48, height: 48 }
                            default:
                                return { url: entityUnclassifiedSelected, width: 48, height: 48 }
                        }
                    case 'Neutral':
                        if (d.type == 'Drone') {
                            return { url: uavNeutral, width: 1000, height: 1250 }
                        } else {
                            return { url: otherNeutral, width: 1000, height: 1250 }
                        }
                    default:
                        return { url: unknown, width: 1000, height: 1250 }
                }
            },
            pickable: true,
            getSize: 40,
            billboard: false,
            getPosition: (d: Asset) => {
                let position = []
                position.push(d.longitude)
                position.push(d.latitude)
                return position
            },
            onClick: (info: any) => {
                handleDetectionOnClick(mapObject, info.object.name, dispatch)
                addLogEvent(activeSessionId, `selected entity, ${info.object.id}`)
            },
        }),
    )

    //mapObject.addLayer(new MapboxLayer({
    //    type: TextLayer,
    //    id: layerIds.detectionText,
    //    data: detectionData,
    //    getPosition: d => {
    //        let position = []
    //        position.push(d.longitude || d.waypoints[0].longitude)
    //        position.push(d.latitude || d.waypoints[0].latitude)
    //        return position
    //    },
    //    getText: d => d.name,
    //    getSize: 12,
    //    getAngle: 0,
    //    billboard: true,
    //    getPixelOffset: [0,25],
    //    fontSettings: {sdf: true},
    //    getColor: [255,255,255,255],
    //    outlineColor: [0,0,0,255],
    //    outlineWidth: 1,
    //    getTextAnchor: 'middle',
    //}))

    return layerIds.detectionMarkers
}

export function createAreaLayer(mapObject: Map, areaData: any, firstLabelLayerId: string) {
    let fill_color = area_gray.fill
    let line_color = area_gray.line

    switch (areaData.type) {
        case area_types.buffer:
            fill_color = area_yellow.fill
            line_color = area_yellow.line
            break
        case area_types.protected:
            fill_color = area_red.fill
            line_color = area_red.line
            break
        default:
            fill_color = area_gray.fill
            line_color = area_gray.line
    }

    mapObject.addLayer(
        new MapboxLayer({
            id: layerIds.areas + areaData.id,
            // @ts-ignore 2025-05-26 Note ST - Getting an error here about non-existent types, but still seems OK.
            // `@deck.gl` type definitions were still a WIP in v8.x, so warning is to be taken with a grain of salt.
            // Source - https://deck.gl/docs/get-started/using-with-typescript
            type: GeoJsonLayer,
            data: areaData.coordinates,
            stroked: false,
            filled: true,
            pointType: 'circle',
            lineWidthScale: 20,
            lineWidthMinPixels: 2,
            getFillColor: fill_color,
            getLineColor: line_color,
            getLineWidth: 1,
        }),
        firstLabelLayerId,
    )

    return {
        name: areaData.name,
        id: areaData.id,
    }
}

export function createRadarLayer(mapObject: Map, radarCenter: Position, firstLabelLayerId: string) {
    //TODO: REMOVE AND/OR REFACTOR ONCE THE REAL DATA IS AVAILABLE
    const radar_center = turf.point(radarCenter)
    let buffer = turf.buffer(radar_center, 800, { units: 'meters' })
    let bbox = turf.bbox(buffer)
    let radar_bounds = turf.bboxPolygon(bbox)

    mapObject.addLayer(
        new MapboxLayer({
            id: layerIds.radar,
            // @ts-ignore 2025-05-26 Note ST - Getting an error here about non-existent types, but still seems OK.
            // `@deck.gl` type definitions were still a WIP in v8.x, so warning is to be taken with a grain of salt.
            // Source - https://deck.gl/docs/get-started/using-with-typescript
            type: BitmapLayer,
            bounds: [
                radar_bounds.geometry.coordinates[0][0],
                radar_bounds.geometry.coordinates[0][3],
                radar_bounds.geometry.coordinates[0][2],
                radar_bounds.geometry.coordinates[0][1],
            ],
            tintColor: [245, 245, 245],
            transparentColor: [245, 245, 245, 10],
            image: radarImage,
        }),
        firstLabelLayerId,
    )

    return { name: 'Radar', id: 'radar' }
}

// UPDATE LAYER METHODS

export async function updateEntities(mapObject: Map, session_id: number, dispatch: Dispatch<AnyAction>) {
    try {
        const updated_data = await getMissionEntities(session_id)

        const blue_layer_updated_data: Partial<Asset>[] = []
        const detection_layer_updated_data: Asset[] = []
        const detection_names_list: Partial<Asset>[] = []

        let unselectEntity = false

        if (updated_data) {
            updated_data.map((entity: Asset) => {
                if (entity.alliance == 'Friendly') {
                    const updated_waypoints = []

                    const updated_waypoint = {
                        latitude: entity.latitude,
                        longitude: entity.longitude,
                        velocity: entity.velocity,
                        altitude: entity.altitude,
                    }

                    updated_waypoints.push(updated_waypoint)

                    const updated_entity = {
                        name: entity.name,
                        type: entity.type,
                        alliance: entity.alliance,
                        waypoints: updated_waypoints,
                    }

                    blue_layer_updated_data.push(updated_entity)
                } else {
                    if (entity.visible) {
                        detection_layer_updated_data.push(entity)
                        detection_names_list.push({
                            name: entity.name,
                            alliance: entity.alliance,
                            type: entity.type,
                        })
                    } else {
                        if (entity.name == selectedDetectedEntity) unselectEntity = true
                    }
                }
            })

            let blueMarkersLayer = mapObject.getLayer(layerIds.blueDronesMarkers)
            let blueTextLayer = mapObject.getLayer(layerIds.blueDronesText)
            let detectionsMarkersLayer = mapObject.getLayer(layerIds.detectionMarkers)
            //let detectionsTextLayer = mapObject.getLayer(layerIds.detectionText)

            // @ts-ignore 2025-05-23 Note ST - Unsure of whether these are broken or not. Leaving these as-is for now.
            blueMarkersLayer.implementation.setProps({ data: blue_layer_updated_data })
            // @ts-ignore
            blueTextLayer.implementation.setProps({ data: blue_layer_updated_data })
            // @ts-ignore
            detectionsMarkersLayer.implementation.setProps({ data: detection_layer_updated_data })
            //detectionsTextLayer.implementation.setProps({data: detection_layer_updated_data})

            if (unselectEntity) {
                stopUpdates()
                dispatch(allActions.assetActions.resetSelectedDetection())
                updateIcons(mapObject, '')
                selectedDetectedEntity = ''
                unselectEntity = false
            }

            dispatch(allActions.assetActions.updateDetectionList(detection_names_list))
        }
    } catch (error) {
        console.log('Error updating entities layers: ', error)
    }
}

export function handleBlueDroneOnClick(mapObject: Map, droneName: string, dispatch: Dispatch<AnyAction>) {
    let iconsLayer = mapObject.getLayer(layerIds.blueDronesMarkers)

    // @ts-ignore 2025-05-23 Note ST - Unsure of whether this is broken or not. Leaving it as-is for now.
    iconsLayer.implementation.setProps({
        getIcon: (d: Asset) => {
            if (d.name == droneName) {
                return { url: uavBlueSelected, width: 1000, height: 1250 }
            }

            return { url: uavBlue, width: 1000, height: 1250 }
        },
    })

    stopBlueAssetUpdates()
    openAssetsDrawer(dispatch)
    updateSelectedBlueDroneState(iconsLayer, dispatch, droneName)
}

function updateIcons(mapObject: Map, droneName: string) {
    let detectionIconsLayer = mapObject.getLayer(layerIds.detectionMarkers)

    // @ts-ignore 2025-05-23 Note ST - Unsure of whether this is broken or not. Leaving it as-is for now.
    detectionIconsLayer.implementation.setProps({
        getIcon: (d: Asset) => {
            if (d.name == droneName) {
                switch (d.alliance) {
                    case 'Enemy':
                        switch (d.threat_level) {
                            case 'Unclassified':
                                return { url: entityUnclassifiedSelected, width: 48, height: 48 }
                            case 'Friendly':
                                return { url: entityFriendlySelected, width: 48, height: 48 }
                            case 'Uncertain':
                                return { url: entityUncertainSelected, width: 48, height: 48 }
                            case 'Suspect':
                                return { url: entitySuspectSelected, width: 48, height: 48 }
                            default:
                                return { url: entityUnclassifiedSelected, width: 48, height: 48 }
                        }
                    case 'Neutral':
                        if (d.type == 'Drone') {
                            return { url: uavNeutralSelected, width: 1000, height: 1250 }
                        } else {
                            return { url: otherNeutralSelected, width: 1000, height: 1250 }
                        }
                    default:
                        return { url: unknownSelected, width: 1000, height: 1250 }
                }
            }

            switch (d.alliance) {
                case 'Enemy':
                    switch (d.threat_level) {
                        case 'Unclassified':
                            return { url: entityUnclassified, width: 48, height: 48 }
                        case 'Friendly':
                            return { url: entityFriendly, width: 48, height: 48 }
                        case 'Uncertain':
                            return { url: entityUncertain, width: 48, height: 48 }
                        case 'Suspect':
                            return { url: entitySuspect, width: 48, height: 48 }
                        default:
                            return { url: entityUnclassified, width: 1000, height: 1000 }
                    }
                case 'Neutral':
                    if (d.type == 'Drone') {
                        return { url: uavNeutral, width: 1000, height: 1250 }
                    } else {
                        return { url: otherNeutral, width: 1000, height: 1250 }
                    }
                default:
                    return { url: unknown, width: 1000, height: 1250 }
            }
        },
    })
}

export function handleDetectionOnClick(mapObject: Map, droneName: string, dispatch: Dispatch<AnyAction>) {
    updateIcons(mapObject, droneName)
    stopDetectionAssetUpdates()
    openDetectionsDrawer(dispatch)
    updateSelectedDetectedState(mapObject.getLayer(layerIds.detectionMarkers), dispatch, droneName)
}

function stopBlueAssetUpdates() {
    clearInterval(interval)
}

function stopDetectionAssetUpdates() {
    clearInterval(detectionsInterval)
}

export function stopUpdates() {
    stopBlueAssetUpdates()
    stopDetectionAssetUpdates()
}

function openAssetsDrawer(dispatch: Dispatch<AnyAction>) {
    dispatch(allActions.drawersActions.openAssetsDrawer())
}

function openDetectionsDrawer(dispatch: Dispatch<AnyAction>) {
    dispatch(allActions.drawersActions.openDetectionsDrawer())
}

function updateSelectedBlueDroneState(layer: AnyLayer, dispatch: Dispatch<AnyAction>, droneName: string) {
    interval = setInterval(
        () => {
            // @ts-ignore 2025-05-23 Note ST - Unsure of whether this is broken or not. Leaving it as-is for now.
            const layerDronesData = layer.implementation.props.data

            const drone = layerDronesData.find((x: any) => x.name === droneName)

            const updateData = {
                name: drone.name,
                id: drone.id,
                latitude: drone?.waypoints[0]?.latitude?.toFixed(8),
                longitude: drone?.waypoints[0]?.longitude?.toFixed(8),
                velocity: drone?.waypoints[0]?.velocity,
                altitude: drone?.waypoints[0]?.altitude?.toFixed(1),
                tilt: drone?.waypoints[0]?.tilt,
                pitch: drone?.waypoints[0]?.pitch,
                payload: drone?.waypoints[0]?.payload,
            }

            dispatch(allActions.assetActions.updateSelectedAsset(updateData))
        },
        droneUpdateSpeed as unknown as number,
    )
}

function updateSelectedDetectedState(layer: AnyLayer, dispatch: Dispatch<AnyAction>, droneName: string) {
    selectedDetectedEntity = droneName

    detectionsInterval = setInterval(
        () => {
            // @ts-ignore 2025-05-23 Note ST - Unsure of whether this is broken or not. Leaving it as-is for now.
            const detectionsLayerData = layer.implementation.props.data

            const drone = detectionsLayerData.find((x: any) => x.name === droneName)

            dispatch(allActions.assetActions.updateSelectedDetection(drone))
        },
        droneUpdateSpeed as unknown as number,
    )
}
